import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button } from '@/components/Button/Button';
import { ApiError, NetworkError } from '@/lib/api';
import { t } from '@/lib/i18n';
import type { EnhanceKind, AddWizardValues, StaffProfileDetail } from '../../schemas';
import { useEnhance } from '../../hooks';
import { EnhancePromptModal } from '../EnhancePromptModal';
import styles from './StepBrief.module.css';

export interface StepBriefProps {
  /** Cached Staff Profile detail; used by Reset-to-defaults. */
  profileDetail?: StaffProfileDetail | null;
}

// The two form fields that have Enhance-with-AI in this step. Matches the
// EditForm pattern so the UX is identical across the hire wizard and edit.
type EnhanceField = 'personality' | 'systemPrompt';

const ENHANCE_FIELD_TO_KIND: Record<EnhanceField, EnhanceKind> = {
  personality: 'member.personality',
  systemPrompt: 'member.instructions',
};

const ENHANCE_FIELD_LABEL: Record<EnhanceField, string> = {
  personality: 'How they work',
  systemPrompt: 'Detailed instructions',
};

export function StepBrief({ profileDetail = null }: StepBriefProps) {
  const { register, watch, setValue, getValues } = useFormContext<AddWizardValues>();
  const firstName = watch('first_name');

  const enhance = useEnhance();
  const [enhanceTarget, setEnhanceTarget] = useState<EnhanceField | null>(null);
  const [enhanceError, setEnhanceError] = useState<string | null>(null);

  function openEnhanceFor(field: EnhanceField) {
    setEnhanceError(null);
    setEnhanceTarget(field);
  }

  function runEnhance(userRequest: string) {
    if (!enhanceTarget) return;
    setEnhanceError(null);
    const field = enhanceTarget;
    const current = getValues(field) ?? '';
    enhance.mutate(
      { kind: ENHANCE_FIELD_TO_KIND[field], content: current, userRequest },
      {
        onSuccess: (response) => {
          setValue(field, response.content, { shouldDirty: true, shouldValidate: true });
          setEnhanceTarget(null);
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 403) {
            setEnhanceError(t("You don't have permission to use Enhance with AI."));
          } else if (err instanceof NetworkError) {
            setEnhanceError(t("Couldn't reach the server. Check your connection and try again."));
          } else {
            setEnhanceError(t('Enhance failed. Try again.'));
          }
        },
      },
    );
  }

  function resetDefaults() {
    if (!profileDetail) return;
    setValue('personality', profileDetail.personality ?? '');
    setValue('systemPrompt', profileDetail.systemPrompt);
  }

  return (
    <section data-testid="step-brief">
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.stepLabel}>{t('STEP 4')}</div>
          <h1 className={styles.title}>
            {t('How should {name} work?', { name: firstName || t('they') })}
          </h1>
          <p className={styles.subtitle}>
            {t(
              "We pre-filled the brief with the position's defaults. Adjust to fit your team's voice.",
            )}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="md"
          onClick={resetDefaults}
          disabled={!profileDetail}
        >
          {t('Reset to defaults')}
        </Button>
      </div>

      <div className={styles.card}>
        <div className={styles.field}>
          <div className={styles.fieldHead}>
            <label className={styles.label} htmlFor="hire-wizard-personality">
              {t('How they work')}
            </label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => openEnhanceFor('personality')}
            >
              ✨ {t('Enhance with AI')}
            </Button>
          </div>
          <textarea
            id="hire-wizard-personality"
            {...register('personality')}
            className={[styles.textarea, styles.textareaPersonality].join(' ')}
          />
          <p className={styles.hint}>{t('Personality and working style. 1–2 sentences.')}</p>
        </div>

        <div className={styles.field}>
          <div className={styles.fieldHead}>
            <label className={styles.label} htmlFor="hire-wizard-instructions">
              {t('Detailed instructions')}
            </label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => openEnhanceFor('systemPrompt')}
            >
              ✨ {t('Enhance with AI')}
            </Button>
          </div>
          <textarea
            id="hire-wizard-instructions"
            {...register('systemPrompt')}
            className={[styles.textarea, styles.textareaInstructions].join(' ')}
          />
          <p className={styles.hint}>
            {t(
              "The full brief. Talk like you're onboarding a new hire — no prompt engineering required.",
            )}
          </p>
        </div>
      </div>

      {enhanceTarget ? (
        <EnhancePromptModal
          fieldLabel={t(ENHANCE_FIELD_LABEL[enhanceTarget])}
          isPending={enhance.isPending}
          error={enhanceError}
          onCancel={() => setEnhanceTarget(null)}
          onConfirm={runEnhance}
        />
      ) : null}
    </section>
  );
}
